import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { exportCSV, exportExcel, exportPDF, printReport } from '../lib/reportExportUtils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  BarChart3, Download, Loader2, ShoppingCart, Boxes, DollarSign,
  FileText, Stethoscope, Users, TrendingUp, TrendingDown,
  AlertTriangle, Package, Receipt, Shield, Building2, Clock, Search,
  FileSpreadsheet, Printer, ChevronDown, ShieldCheck
} from 'lucide-react';
import { fmtCurrency, fmtDate, fmtNumber } from '../lib/localeUtils';
import { keyed } from '../lib/listKeys';
import { OfflineSalesReportCard } from './reports/OfflineSalesReportCard';

const REPORT_CATEGORIES = [
  {
    id: 'sales', label: 'Sales', icon: ShoppingCart,
    reports: [
      { id: 'sales-summary', label: 'Sales Summary', desc: 'Revenue, profit, and units by period', hasGroupBy: true },
      { id: 'sales-by-payment', label: 'By Payment Method', desc: 'Breakdown by cash, credit, insurance, corporate' },
      { id: 'sales-by-category', label: 'By Product Category', desc: 'Revenue breakdown by drug category' },
      { id: 'top-products', label: 'Top Selling Products', desc: 'Ranked by revenue and quantity' },
    ]
  },
  {
    id: 'inventory', label: 'Inventory', icon: Boxes,
    reports: [
      { id: 'stock-levels', label: 'Stock Levels', desc: 'Current stock with alerts', noDate: true },
      { id: 'expiring-products', label: 'Expiring Products', desc: 'Products nearing or past expiry', hasDays: true },
      { id: 'dead-stock', label: 'Dead Stock', desc: 'Unsold products tying up capital', hasDays: true },
    ]
  },
  {
    id: 'financial', label: 'Financial', icon: DollarSign,
    reports: [
      { id: 'profit-loss', label: 'Profit & Loss', desc: 'Revenue, COGS, and net profit' },
      { id: 'cash-register', label: 'Cash Register', desc: 'Daily cash, credit, and claims breakdown' },
    ]
  },
  {
    id: 'billing', label: 'Billing', icon: FileText,
    reports: [
      { id: 'insurance-claims', label: 'Insurance Claims', desc: 'All insurance claims with provider summary' },
      { id: 'corporate-bills', label: 'Corporate Bills', desc: 'All corporate billing with client summary' },
      { id: 'overdue-receivables', label: 'Overdue Receivables', desc: 'Unpaid insurance & corporate bills', hasDays: true },
      { id: 'receipt-history', label: 'Receipt History', desc: 'Complete transaction receipt log' },
    ]
  },
  {
    id: 'clinical', label: 'Clinical', icon: Stethoscope,
    reports: [
      { id: 'prescription-volume', label: 'Prescription Volume', desc: 'Rx vs OTC daily trends' },
      { id: 'controlled-substances', label: 'Controlled Substances', desc: 'Dispensing audit log for Rx products' },
      { id: 'controlled-drugs', label: 'Controlled Drugs Register', desc: 'Track all controlled substance dispensing with patient details' },
    ]
  },
  {
    id: 'catalog', label: 'Catalog', icon: Boxes,
    reports: [
      { id: 'categories-report', label: 'Categories Overview', desc: 'All categories with product count, stock, and value', noDate: true },
      { id: 'manufacturers-report', label: 'Manufacturers Overview', desc: 'All manufacturers with product count and inventory value', noDate: true },
      { id: 'products-by-manufacturer', label: 'Products by Manufacturer', desc: 'Filter products by specific manufacturer', noDate: true, hasManufacturer: true },
    ]
  },
  {
    id: 'operations', label: 'Operations', icon: Users,
    reports: [
      { id: 'user-activity', label: 'User Activity', desc: 'Staff actions, logins, and productivity' },
      { id: 'supplier-performance', label: 'Supplier Performance', desc: 'Order volume, spend, and lead times', noDate: true },
      { id: 'supplier-payments', label: 'Supplier Payment Report', desc: 'Periodic supplier payment history with method breakdown' },
    ]
  },
];

function formatCurrency(val) {
  return fmtCurrency(val);
}

function SummaryCards({ totals, reportId }) {
  if (!totals) return null;

  const cardDefs = {
    'sales-summary': [
      { label: 'Total Revenue', value: formatCurrency(totals.total_revenue), icon: DollarSign, color: 'text-emerald-500' },
      { label: 'Total Profit', value: formatCurrency(totals.total_profit), icon: TrendingUp, color: 'text-blue-500' },
      { label: 'Transactions', value: totals.total_transactions, icon: Receipt, color: 'text-purple-500' },
      { label: 'Units Sold', value: totals.total_units, icon: Package, color: 'text-amber-500' },
    ],
    'sales-by-payment': [
      { label: 'Total Revenue', value: formatCurrency(totals.total_revenue), icon: DollarSign, color: 'text-emerald-500' },
      { label: 'Transactions', value: totals.total_transactions, icon: Receipt, color: 'text-purple-500' },
    ],
    'sales-by-category': [
      { label: 'Total Revenue', value: formatCurrency(totals.total_revenue), icon: DollarSign, color: 'text-emerald-500' },
    ],
    'top-products': [
      { label: 'Products Sold', value: totals.products_sold, icon: Package, color: 'text-blue-500' },
    ],
    'stock-levels': [
      { label: 'Total Products', value: totals.total_products, icon: Package, color: 'text-blue-500' },
      { label: 'Out of Stock', value: totals.out_of_stock, icon: AlertTriangle, color: 'text-red-500' },
      { label: 'Critical', value: totals.critical, icon: TrendingDown, color: 'text-amber-500' },
      { label: 'Inventory Value', value: formatCurrency(totals.total_value_cost), icon: DollarSign, color: 'text-emerald-500' },
    ],
    'expiring-products': [
      { label: 'Expired', value: totals.expired, icon: AlertTriangle, color: 'text-red-500' },
      { label: 'Expiring Soon', value: totals.expiring_soon, icon: Clock, color: 'text-amber-500' },
      { label: 'At-Risk Value', value: formatCurrency(totals.total_at_risk_value), icon: DollarSign, color: 'text-red-500' },
    ],
    'dead-stock': [
      { label: 'Dead Products', value: totals.dead_products, icon: Package, color: 'text-red-500' },
      { label: 'Stuck Value', value: formatCurrency(totals.total_stuck_value), icon: DollarSign, color: 'text-amber-500' },
    ],
    'profit-loss': [
      { label: 'Revenue', value: formatCurrency(totals.revenue), icon: DollarSign, color: 'text-emerald-500' },
      { label: 'Gross Profit', value: formatCurrency(totals.gross_profit), icon: TrendingUp, color: 'text-blue-500' },
      { label: 'Net Profit', value: formatCurrency(totals.net_profit), icon: TrendingUp, color: 'text-purple-500' },
      { label: 'Margin', value: `${totals.gross_margin}%`, icon: BarChart3, color: 'text-amber-500' },
    ],
    'cash-register': [
      { label: 'Cash', value: formatCurrency(totals.cash), icon: DollarSign, color: 'text-emerald-500' },
      { label: 'Credit/Card', value: formatCurrency(totals.credit), icon: Receipt, color: 'text-blue-500' },
      { label: 'Insurance', value: formatCurrency(totals.insurance_claim), icon: Shield, color: 'text-purple-500' },
      { label: 'Net Total', value: formatCurrency(totals.net_total), icon: TrendingUp, color: 'text-amber-500' },
    ],
    'insurance-claims': [
      { label: 'Total Claims', value: totals.total_claims, icon: Shield, color: 'text-blue-500' },
      { label: 'Total Billed', value: formatCurrency(totals.total_billed), icon: DollarSign, color: 'text-emerald-500' },
      { label: 'Pending', value: totals.pending, icon: Clock, color: 'text-amber-500' },
      { label: 'Settled', value: totals.settled, icon: TrendingUp, color: 'text-emerald-500' },
    ],
    'corporate-bills': [
      { label: 'Total Bills', value: totals.total_bills, icon: Building2, color: 'text-blue-500' },
      { label: 'Total Billed', value: formatCurrency(totals.total_billed), icon: DollarSign, color: 'text-emerald-500' },
      { label: 'Invoiced', value: totals.invoiced, icon: Clock, color: 'text-amber-500' },
      { label: 'Paid', value: totals.paid, icon: TrendingUp, color: 'text-emerald-500' },
    ],
    'overdue-receivables': [
      { label: 'Overdue Count', value: totals.total_overdue, icon: AlertTriangle, color: 'text-red-500' },
      { label: 'Total Overdue', value: formatCurrency(totals.total_amount), icon: DollarSign, color: 'text-red-500' },
      { label: 'Insurance', value: totals.insurance_overdue, icon: Shield, color: 'text-amber-500' },
      { label: 'Corporate', value: totals.corporate_overdue, icon: Building2, color: 'text-amber-500' },
    ],
    'receipt-history': [
      { label: 'Receipts', value: totals.total_receipts, icon: Receipt, color: 'text-blue-500' },
      { label: 'Total Amount', value: formatCurrency(totals.total_amount), icon: DollarSign, color: 'text-emerald-500' },
      { label: 'Discounts', value: formatCurrency(totals.total_discount), icon: TrendingDown, color: 'text-amber-500' },
    ],
    'prescription-volume': [
      { label: 'Prescriptions', value: totals.total_prescriptions, icon: Stethoscope, color: 'text-blue-500' },
      { label: 'OTC Sales', value: totals.total_otc, icon: ShoppingCart, color: 'text-emerald-500' },
      { label: 'Rx %', value: `${totals.rx_percentage}%`, icon: BarChart3, color: 'text-purple-500' },
    ],
    'controlled-substances': [
      { label: 'Dispensed', value: totals.total_dispensed, icon: AlertTriangle, color: 'text-red-500' },
      { label: 'Unique Products', value: totals.unique_products, icon: Package, color: 'text-blue-500' },
    ],
    'user-activity': [
      { label: 'Active Users', value: totals.total_users, icon: Users, color: 'text-blue-500' },
      { label: 'Total Actions', value: totals.total_actions, icon: BarChart3, color: 'text-emerald-500' },
    ],
    'supplier-performance': [
      { label: 'Suppliers', value: totals.total_suppliers, icon: Users, color: 'text-blue-500' },
      { label: 'Total Spent', value: formatCurrency(totals.total_spent), icon: DollarSign, color: 'text-emerald-500' },
    ],
    'categories-report': [
      { label: 'Categories', value: totals.total_categories, icon: Boxes, color: 'text-blue-500' },
      { label: 'Products', value: totals.total_products, icon: Package, color: 'text-emerald-500' },
      { label: 'Stock Units', value: fmtNumber(totals.total_stock_units), icon: Package, color: 'text-purple-500' },
      { label: 'Inventory Value', value: formatCurrency(totals.total_inventory_value), icon: DollarSign, color: 'text-amber-500' },
    ],
    'manufacturers-report': [
      { label: 'Manufacturers', value: totals.total_manufacturers, icon: Building2, color: 'text-blue-500' },
      { label: 'Products', value: totals.total_products, icon: Package, color: 'text-emerald-500' },
      { label: 'Stock Units', value: fmtNumber(totals.total_stock_units), icon: Package, color: 'text-purple-500' },
      { label: 'Inventory Value', value: formatCurrency(totals.total_inventory_value), icon: DollarSign, color: 'text-amber-500' },
    ],
    'products-by-manufacturer': [
      { label: 'Products', value: totals.total_products, icon: Package, color: 'text-blue-500' },
      { label: 'Stock Units', value: fmtNumber(totals.total_stock_units), icon: Package, color: 'text-purple-500' },
      { label: 'Inventory Value', value: formatCurrency(totals.total_inventory_value), icon: DollarSign, color: 'text-amber-500' },
    ],
    'controlled-drugs': [
      { label: 'Controlled Products', value: totals.total_controlled_products, icon: ShieldCheck, color: 'text-rose-500' },
      { label: 'Transactions', value: totals.total_transactions, icon: Package, color: 'text-blue-500' },
      { label: 'Units Dispensed', value: totals.total_units_dispensed, icon: Package, color: 'text-amber-500' },
      { label: 'Without Rx', value: totals.without_prescription, icon: AlertTriangle, color: 'text-destructive' },
    ],
    'supplier-payments': [
      { label: 'Payments', value: totals.total_payments, icon: DollarSign, color: 'text-blue-500' },
      { label: 'Total Paid', value: formatCurrency(totals.total_amount), icon: DollarSign, color: 'text-emerald-500' },
      { label: 'Suppliers', value: totals.suppliers_paid, icon: Users, color: 'text-purple-500' },
      { label: 'Avg Payment', value: formatCurrency(totals.avg_payment), icon: DollarSign, color: 'text-amber-500' },
    ],
  };

  const cards = cardDefs[reportId] || [];
  if (cards.length === 0) return null;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="report-summary-cards">
      {keyed(cards, c => c.label).map(({ item: c, key }) => (
        <Card key={key} className="p-3">
          <div className="flex items-center gap-2">
            <c.icon className={`h-4 w-4 ${c.color}`} />
            <span className="text-xs text-muted-foreground">{c.label}</span>
          </div>
          <p className="text-lg font-bold mt-1">{typeof c.value === 'number' ? fmtNumber(c.value) : (c.value ?? 0)}</p>
        </Card>
      ))}
    </div>
  );
}

function ReportTable({ data, reportId }) {
  if (!data || !data.rows || data.rows.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground" data-testid="report-empty">
        <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-30" />
        <p className="text-sm">No data found for this report.</p>
        <p className="text-xs mt-1">Try adjusting the date range or filters.</p>
      </div>
    );
  }

  const rows = data.rows;
  const cols = Object.keys(rows[0]).filter(k => k !== 'bold' && k !== 'info');

  const formatHeader = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const formatCell = (val, key) => {
    if (val === null || val === undefined) return '-';
    if (typeof val === 'number') {
      if (key.includes('amount') || key.includes('revenue') || key.includes('cost') || key.includes('profit') || key.includes('billed') || key.includes('total') || key.includes('copay') || key.includes('claim') || key.includes('discount') || key.includes('spent') || key.includes('value') || key.includes('cash') || key.includes('credit') || key.includes('insurance') || key.includes('corporate') || key.includes('other') || key.includes('net')) {
        return formatCurrency(val);
      }
      if (key.includes('percentage') || key.includes('margin') || key.includes('rate')) return `${val}%`;
      return fmtNumber(val);
    }
    if (key === 'date' || key === 'expiry_date' || key === 'last_active') return fmtDate(val) || val;
    return String(val);
  };

  const statusBadge = (val) => {
    const variants = {
      'healthy': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
      'low': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      'critical': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      'out_of_stock': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      'expired': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      'expiring': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      'pending': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      'settled': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
      'paid': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
      'invoiced': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    };
    return variants[val] || '';
  };

  return (
    <div className="rounded-lg border overflow-auto max-h-[500px]" data-testid="report-table">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 sticky top-0 z-10">
          <tr>
            {cols.map(col => (
              <th key={col} className="text-left px-3 py-2 font-medium text-xs text-muted-foreground whitespace-nowrap">
                {formatHeader(col)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {keyed(rows).map(({ item: row, key }) => (
            <tr key={key} className={`border-t hover:bg-muted/30 ${row.bold ? 'font-semibold bg-muted/20' : ''} ${row.info ? 'text-muted-foreground italic' : ''}`}>
              {cols.map(col => (
                <td key={col} className="px-3 py-2 whitespace-nowrap">
                  {col === 'status' ? (
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${statusBadge(row[col])}`}>
                      {String(row[col]).replace(/_/g, ' ')}
                    </span>
                  ) : formatCell(row[col], col)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ReportsPage() {
  const [activeCategory, setActiveCategory] = useState('sales');
  const [activeReport, setActiveReport] = useState('sales-summary');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [groupBy, setGroupBy] = useState('day');
  const [days, setDays] = useState(90);
  const [manufacturer, setManufacturer] = useState('');
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');

  const currentCategory = REPORT_CATEGORIES.find(c => c.id === activeCategory);
  const currentReport = currentCategory?.reports.find(r => r.id === activeReport);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (!currentReport?.noDate) {
        if (dateFrom) params.append('date_from', dateFrom);
        if (dateTo) params.append('date_to', dateTo);
      }
      if (currentReport?.hasGroupBy) params.append('group_by', groupBy);
      if (currentReport?.hasDays) params.append('days', days);
      if (currentReport?.hasManufacturer && manufacturer) params.append('manufacturer', manufacturer);

      const res = await api.get(`/reports/${activeReport}?${params.toString()}`);
      setData(res.data);
    } catch (err) {
      console.error('Report fetch failed:', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [activeReport, dateFrom, dateTo, groupBy, days, manufacturer, currentReport]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    if (activeReport === 'products-by-manufacturer') {
      api.get('/products-manufacturers').then(res => {
        setManufacturers(res.data.manufacturers || []);
      }).catch(() => {});
    }
  }, [activeReport]);

  const handleCategoryChange = (catId) => {
    setActiveCategory(catId);
    const cat = REPORT_CATEGORIES.find(c => c.id === catId);
    if (cat?.reports?.length) {
      setActiveReport(cat.reports[0].id);
    }
    setData(null);
  };

  const fileLabel = `${activeReport}_${new Date().toISOString().slice(0, 10)}`;
  const summaryByGroup = data?.by_provider || data?.by_client || null;

  const handleExportCSV = () => exportCSV(data?.rows, fileLabel);
  const handleExportExcel = () => exportExcel(data?.rows, fileLabel, data?.totals);
  const handleExportPDF = async () => {
    try {
      await exportPDF(data?.rows, fileLabel, data?.totals, summaryByGroup);
    } catch {
      toast.error('Could not generate the PDF. Please try again.');
    }
  };
  const handlePrint = () => printReport(data?.rows, fileLabel, data?.totals, summaryByGroup);

  const filteredRows = data?.rows?.filter(row => {
    if (!searchFilter) return true;
    const s = searchFilter.toLowerCase();
    return Object.values(row).some(v => String(v).toLowerCase().includes(s));
  });
  const displayData = filteredRows ? { ...data, rows: filteredRows } : data;

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Reports & Analytics
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Comprehensive business intelligence for your pharmacy
          </p>
        </div>
        {data?.rows?.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="export-dropdown-btn">
                <Download className="h-4 w-4" /> Export <ChevronDown className="h-3.5 w-3.5 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={handleExportCSV} data-testid="export-csv-btn">
                <FileText className="mr-2 h-4 w-4" /> Export CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportExcel} data-testid="export-excel-btn">
                <FileSpreadsheet className="mr-2 h-4 w-4" /> Export Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportPDF} data-testid="export-pdf-btn">
                <Download className="mr-2 h-4 w-4" /> Export PDF
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handlePrint} data-testid="print-report-btn">
                <Printer className="mr-2 h-4 w-4" /> Print Report
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <OfflineSalesReportCard />

      {/* Category Tabs */}
      <Tabs value={activeCategory} onValueChange={handleCategoryChange} className="space-y-4">
        <TabsList className="flex w-full overflow-x-auto" data-testid="report-category-tabs">
          {REPORT_CATEGORIES.map(cat => (
            <TabsTrigger key={cat.id} value={cat.id} className="gap-1.5 text-xs sm:text-sm flex-shrink-0" data-testid={`tab-${cat.id}`}>
              <cat.icon className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{cat.label}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {REPORT_CATEGORIES.map(cat => (
          <TabsContent key={cat.id} value={cat.id} className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <cat.icon className="h-4 w-4 text-primary" /> {cat.label} Reports
                </CardTitle>
                <CardDescription>Select a report and configure filters</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Report selector + Filters */}
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="space-y-1 min-w-[200px]">
                    <Label className="text-xs">Report</Label>
                    <Select value={activeReport} onValueChange={setActiveReport}>
                      <SelectTrigger className="h-9" data-testid="report-selector">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {cat.reports.map(r => (
                          <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {!currentReport?.noDate && (
                    <>
                      <div className="space-y-1">
                        <Label className="text-xs">From</Label>
                        <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 w-36" data-testid="date-from" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">To</Label>
                        <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 w-36" data-testid="date-to" />
                      </div>
                    </>
                  )}

                  {currentReport?.hasGroupBy && (
                    <div className="space-y-1">
                      <Label className="text-xs">Group By</Label>
                      <Select value={groupBy} onValueChange={setGroupBy}>
                        <SelectTrigger className="h-9 w-28" data-testid="group-by-selector">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="day">Day</SelectItem>
                          <SelectItem value="week">Week</SelectItem>
                          <SelectItem value="month">Month</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {currentReport?.hasDays && (
                    <div className="space-y-1">
                      <Label className="text-xs">Days</Label>
                      <Input type="number" value={days} onChange={e => setDays(parseInt(e.target.value) || 30)} className="h-9 w-20" min={1} max={365} data-testid="days-input" />
                    </div>
                  )}

                  {currentReport?.hasManufacturer && (
                    <div className="space-y-1 min-w-[180px]">
                      <Label className="text-xs">Manufacturer</Label>
                      <Select value={manufacturer} onValueChange={setManufacturer}>
                        <SelectTrigger className="h-9" data-testid="manufacturer-filter">
                          <SelectValue placeholder="All Manufacturers" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all_mfr">All Manufacturers</SelectItem>
                          {manufacturers.map(m => (
                            <SelectItem key={m} value={m}>{m}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <Button onClick={fetchReport} disabled={loading} className="h-9 gap-1.5" data-testid="generate-report-btn">
                    {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BarChart3 className="h-3.5 w-3.5" />}
                    Generate
                  </Button>
                </div>

                {currentReport && (
                  <p className="text-xs text-muted-foreground">{currentReport.desc}</p>
                )}
              </CardContent>
            </Card>

            {/* Results */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : data ? (
              <div className="space-y-4">
                <SummaryCards totals={data.totals} reportId={activeReport} />

                {/* Provider/Client summary for billing reports */}
                {data.by_provider && data.by_provider.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">By Insurance Provider</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="rounded-lg border overflow-auto max-h-[200px]">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/50 sticky top-0">
                            <tr>
                              <th className="text-left px-3 py-1.5 text-xs font-medium text-muted-foreground">Provider</th>
                              <th className="text-left px-3 py-1.5 text-xs font-medium text-muted-foreground">Claims</th>
                              <th className="text-left px-3 py-1.5 text-xs font-medium text-muted-foreground">Billed</th>
                              <th className="text-left px-3 py-1.5 text-xs font-medium text-muted-foreground">Claims Amt</th>
                              <th className="text-left px-3 py-1.5 text-xs font-medium text-muted-foreground">Copay</th>
                              <th className="text-left px-3 py-1.5 text-xs font-medium text-muted-foreground">Patients</th>
                            </tr>
                          </thead>
                          <tbody>
                            {keyed(data.by_provider, p => p.provider_name).map(({ item: p, key }) => (
                              <tr key={key} className="border-t">
                                <td className="px-3 py-1.5 font-medium">{p.provider_name}</td>
                                <td className="px-3 py-1.5">{p.claims}</td>
                                <td className="px-3 py-1.5">{formatCurrency(p.total_billed)}</td>
                                <td className="px-3 py-1.5">{formatCurrency(p.total_claims)}</td>
                                <td className="px-3 py-1.5">{formatCurrency(p.total_copay)}</td>
                                <td className="px-3 py-1.5">{p.patients}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {data.by_client && data.by_client.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">By Corporate Client</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="rounded-lg border overflow-auto max-h-[200px]">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/50 sticky top-0">
                            <tr>
                              <th className="text-left px-3 py-1.5 text-xs font-medium text-muted-foreground">Client</th>
                              <th className="text-left px-3 py-1.5 text-xs font-medium text-muted-foreground">Bills</th>
                              <th className="text-left px-3 py-1.5 text-xs font-medium text-muted-foreground">Billed</th>
                              <th className="text-left px-3 py-1.5 text-xs font-medium text-muted-foreground">Discount</th>
                              <th className="text-left px-3 py-1.5 text-xs font-medium text-muted-foreground">Employees</th>
                            </tr>
                          </thead>
                          <tbody>
                            {keyed(data.by_client, c => c.client_name).map(({ item: c, key }) => (
                              <tr key={key} className="border-t">
                                <td className="px-3 py-1.5 font-medium">{c.client_name}</td>
                                <td className="px-3 py-1.5">{c.bills}</td>
                                <td className="px-3 py-1.5">{formatCurrency(c.total_billed)}</td>
                                <td className="px-3 py-1.5">{formatCurrency(c.total_discount)}</td>
                                <td className="px-3 py-1.5">{c.employees}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Search filter */}
                {data?.rows?.length > 5 && (
                  <div className="flex items-center gap-2">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Filter results..."
                      value={searchFilter}
                      onChange={e => setSearchFilter(e.target.value)}
                      className="h-8 w-64"
                      data-testid="report-search-filter"
                    />
                    {searchFilter && (
                      <Badge variant="secondary" className="text-xs">
                        {filteredRows?.length} / {data.rows.length}
                      </Badge>
                    )}
                  </div>
                )}

                <ReportTable data={displayData} reportId={activeReport} />
              </div>
            ) : null}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
